import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { m } from 'framer-motion';
import { Stack, MenuItem, IconButton, Box, Typography, Divider } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Iconify, MenuPopover } from '../../../components';
import { varHover } from '../../../components/animate';
import { useGetCurrentUser } from '../../../hooks';
import { PATH_DASHBOARD } from '../../../routes/paths';

const RED = '#B72136';

// Super admin menu is INTENTIONALLY a fixed list — every entry is a
// system-level tool, not a practice-scoped feature. The previous
// version gated Consent (V1) and Form Builder (V2) on
// ``useFormsFlags`` which reads per-practice permissions; that meant
// when a super admin was scoped to a practice without V2, the Form
// Builder link disappeared from their menu even though the page is
// fully reachable and the operator needs it to manage system-level
// templates. Removed the gates.
const getOptions = () => [
    {
        label: 'Feature Flags',
        value: 'super_admin_feature_flags',
        linkTo: PATH_DASHBOARD.superAdmin.featureFlags,
        icon: 'solar:flag-bold-duotone',
    },
    {
        label: 'Chatbot Management',
        value: 'super_admin_chatbot_management',
        linkTo: PATH_DASHBOARD.superAdmin.chatbotManagement,
        icon: 'solar:chat-round-dots-bold-duotone',
    },
    {
        label: 'WhatsApp AI Bot',
        value: 'super_admin_whatsapp_ai_bot',
        linkTo: PATH_DASHBOARD.superAdmin.whatsappAiBot,
        icon: 'solar:magic-stick-3-bold-duotone',
    },
    // Tasks V2 Migration — retired from the UI 2026-08-01 (V1 sunset complete).
    // Route + lazy import are commented out in src/routes/index.jsx; this entry
    // is kept, not deleted, so the cockpit can be restored by uncommenting both.
    // {
    //     label: 'Tasks V2 Migration',
    //     value: 'super_admin_tasks_v2_migration',
    //     linkTo: PATH_DASHBOARD.superAdmin.tasksV2Migration,
    //     icon: 'solar:checklist-minimalistic-bold-duotone',
    // },
    {
        label: 'Create Practice',
        value: 'super_admin_create_practice',
        linkTo: PATH_DASHBOARD.superAdmin.createPractice,
        icon: 'solar:buildings-2-bold-duotone',
    },
    {
        label: 'User Management',
        value: 'super_admin_user_management',
        linkTo: PATH_DASHBOARD.superAdmin.UserManagement,
        icon: 'solar:users-group-two-rounded-bold-duotone',
    },
    {
        label: 'Domain Management',
        value: 'super_admin_domain_management',
        linkTo: PATH_DASHBOARD.superAdmin.DomainManagement,
        icon: 'solar:global-bold-duotone',
    },
    {
        label: 'Practice Choices',
        value: 'super_admin_practice_choices',
        linkTo: PATH_DASHBOARD.superAdmin.PracticeChoices,
        icon: 'solar:list-check-bold-duotone',
    },
    {
        label: 'Consent',
        value: 'super_admin_consent',
        linkTo: PATH_DASHBOARD.superAdmin.consent,
        icon: 'solar:document-add-bold-duotone',
    },
    {
        label: 'Form Builder',
        value: 'super_admin_consent_builder',
        linkTo: PATH_DASHBOARD.superAdmin.consentV2,
        icon: 'solar:document-text-bold-duotone',
    },
    // 'Auto Reminder' entry removed in Phase 1 of PracticeAutoReminder deprecation
    // (see /Users/admin/Desktop/2026-06-08-auto-reminder-removal-rca.md).
    {
        label: 'Import Leads',
        value: 'super_admin_import_leads',
        linkTo: PATH_DASHBOARD.superAdmin.ImportLeads,
        icon: 'solar:upload-minimalistic-bold-duotone',
    },
    {
        label: 'Engagement Templates',
        value: 'super_admin_engagement',
        linkTo: PATH_DASHBOARD.superAdmin.engagementTemplates,
        icon: 'solar:letter-bold-duotone',
    },
    {
        label: 'Group Dashboard',
        value: 'super_admin_group_dashboard',
        linkTo: PATH_DASHBOARD.superAdmin.groupDashboard,
        icon: 'solar:buildings-3-bold-duotone',
    },
    {
        label: 'PMS',
        value: 'super_admin_pms_settings',
        linkTo: PATH_DASHBOARD.superAdmin.pmsSettings,
        icon: 'solar:calendar-bold-duotone',
    },
    {
        label: 'Table Defaults',
        value: 'super_admin_table_defaults',
        linkTo: PATH_DASHBOARD.superAdmin.tableDefaults,
        icon: 'solar:widget-2-bold-duotone',
    },
    {
        label: 'Sidebar Navigation',
        value: 'super_admin_sidebar_navigation',
        linkTo: PATH_DASHBOARD.superAdmin.sidebarNavigation,
        icon: 'solar:sidebar-minimalistic-bold-duotone',
    },
    {
        label: 'System Task Templates',
        value: 'super_admin_system_task_templates',
        linkTo: PATH_DASHBOARD.superAdmin.systemTaskTemplates,
        icon: 'solar:checklist-bold-duotone',
    },
    {
        label: 'Notification Management',
        value: 'super_admin_notification_management',
        linkTo: PATH_DASHBOARD.superAdmin.notificationManagement,
        icon: 'solar:bell-bing-bold-duotone',
    },
];

export default function SuperAdminPopover() {
    const [open, setOpen] = useState(null);
    const { isSuperUser } = useGetCurrentUser();
    const theme = useTheme();

    if (!isSuperUser) return null;

    const options = getOptions();

    return (
        <>
            <IconButton
                component={m.button}
                whileTap="tap"
                whileHover="hover"
                variants={varHover(1.05)}
                aria-label="super admin settings"
                onClick={(event) => setOpen(event.currentTarget)}
                sx={{ width: 40, height: 40 }}
            >
                <Iconify icon="solar:settings-bold-duotone" width={24} sx={{ color: RED }} />
            </IconButton>

            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={() => setOpen(null)}
                sx={{
                    p: 0,
                    mt: 1.5,
                    ml: 0.75,
                    width: 280,
                    maxHeight: '80vh',
                    overflow: 'auto',
                    '& .MuiMenuItem-root': { borderRadius: '8px' },
                }}
            >
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: RED }}>
                        Super Admin
                    </Typography>
                </Box>
                <Divider />
                <Stack sx={{ p: 1 }}>
                    {options.map((option) => (
                        <MenuItem
                            key={option.value}
                            to={option.linkTo}
                            component={RouterLink}
                            onClick={() => setOpen(null)}
                            sx={{
                                px: 1.5,
                                py: 1,
                                minHeight: 'auto',
                                '&:hover': { bgcolor: alpha(RED, 0.08) },
                                '&:hover .sa-icon-box': {
                                    bgcolor: alpha(RED, 0.12),
                                    '& svg': { color: RED },
                                },
                            }}
                        >
                            <Box
                                className="sa-icon-box"
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(theme.palette.grey[500], 0.08),
                                    mr: 1.5,
                                    flexShrink: 0,
                                    transition: 'all 0.2s',
                                }}
                            >
                                <Iconify
                                    icon={option.icon}
                                    width={20}
                                    sx={{ color: theme.palette.text.secondary, transition: 'color 0.2s' }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.4,
                                    flex: 1,
                                }}
                            >
                                {option.label}
                            </Typography>
                        </MenuItem>
                    ))}
                </Stack>
            </MenuPopover>
        </>
    );
}
